<template>
  <div>
    <p
      v-if="showInstructions"
      class="px-4 pt-4 pb-1 text-center blue-grey--text text--darken-3 subtitle-2"
      data-testid="filters-text-instructions"
    >
      {{ $t('common.instructions') }}
    </p>

    <v-btn
      class="ms-1 font-size-12"
      color="blue"
      data-testid="filters-button-clear-all"
      rounded
      small
      style="z-index: 1"
      text
      :style="
        showClearAllFilters ? 'visibility: visible' : 'visibility: hidden'
      "
      @click.stop="clearAllFilters"
    >
      <v-icon class="me-2" size="16">mdi-close</v-icon>
      {{ $t('common.clear_filters') }}
    </v-btn>

    <FilterHeader
      class="mt-4"
      :isActive="showFilterByType.types"
      :name="$t('common.content_type')"
      @change="showFilterByType.types = !showFilterByType.types"
    />
    <v-list
      v-if="showFilterByType.types"
      class="pa-0 ma-0"
      color="white"
      dense
      data-testid="filters-section-types"
    >
      <v-btn
        v-show="isTypeSelected"
        class="mb-1 mt-0 font-size-12 ms-1"
        color="blue"
        data-testid="filters-button-clear-types"
        rounded
        small
        text
        @click="clearFilter([], ['types'])"
      >
        {{ $t('common.clear') }}
      </v-btn>
      <v-list-item
        v-for="item in typeOptions"
        :key="item.id"
        class="ma-0 pa-0 px-5"
        dense
      >
        <div
          class="d-flex justify-space-between align-center"
          style="width: 100%"
        >
          <div class="flex-grow-1">
            <v-checkbox
              class="ma-0 pa-0"
              data-testid="filters-checkbox-type"
              hide-details
              :input-value="selectedFilters.types"
              :value="item.id"
              @change="$emit('change', $event, 'types')"
            >
              <template v-slot:label>
                <p
                  class="ms-2 font-size-13 font-weight-500 blue-grey--text text--darken-3 ma-0 pa-0 align-center"
                >
                  {{ item.text }}
                </p>
              </template>
            </v-checkbox>
          </div>
          <v-btn
            v-if="hasColumnSelector"
            data-testid="filters-button-open-column-selector"
            icon
            x-small
            @click="$emit('toggleColumnSelector', item.id)"
          >
            <v-icon color="blue-grey darken-1" size="24"
              >mdi-chevron-right</v-icon
            >
          </v-btn>
        </div>
      </v-list-item>
    </v-list>

    <FilterHeader
      class="mt-4"
      :isActive="showFilterByType.status"
      :name="$t('common.status')"
      @change="showFilterByType.status = !showFilterByType.status"
    />
    <v-list
      v-if="showFilterByType.status"
      class="pa-0 ma-0"
      color="white"
      dense
      data-testid="filters-section-status"
    >
      <v-btn
        v-show="isStatusSelected"
        class="mb-1 mt-0 font-size-12 ms-1"
        color="blue"
        data-testid="filters-button-clear-status"
        rounded
        small
        text
        @click="clearFilter([], ['status'])"
      >
        {{ $t('common.clear') }}
      </v-btn>
      <v-list-item
        v-for="item in statusOptions"
        :key="item.id"
        class="ma-0 pa-0 px-5"
        dense
      >
        <div style="width: 100%">
          <v-checkbox
            class="ma-0 pa-0"
            data-testid="filters-checkbox-status"
            hide-details
            :input-value="selectedFilters.status"
            :value="item.id"
            @change="$emit('change', $event, 'status')"
          >
            <template v-slot:label>
              <p
                class="ms-2 font-size-13 font-weight-500 blue-grey--text text--darken-3 ma-0 pa-0 align-center"
              >
                {{ item.text }}
              </p>
            </template>
          </v-checkbox>
        </div>
      </v-list-item>
    </v-list>

    <FilterHeader
      class="mt-4"
      :isActive="showFilterByType.validation"
      :name="$t('common.validation')"
      @change="showFilterByType.validation = !showFilterByType.validation"
    />
    <div
      v-if="showFilterByType.validation"
      class="pt-0"
      data-testid="filters-section-validation"
    >
      <v-btn
        v-show="Boolean(selectedValidationOption)"
        class="mb-1 mt-0 font-size-12 ms-1"
        color="blue"
        data-testid="filters-button-clear-validation"
        rounded
        small
        text
        @click="clearValidationFilter"
      >
        {{ $t('common.clear') }}
      </v-btn>
      <RadioFilter
        class="px-4 pt-1"
        :options="validationRadioOptions"
        :value="selectedValidationOption"
        @change="handleValidationOptionChange"
      />
    </div>

    <FilterHeader
      class="mt-4"
      :isActive="showFilterByType.users"
      :name="$t('filters.users')"
      @change="showFilterByType.users = !showFilterByType.users"
    />
    <template v-if="showFilterByType.users">
      <v-btn
        v-show="isUsersSelected"
        class="mb-1 mt-0 font-size-12 ms-1"
        color="blue"
        data-testid="filters-button-clear-users"
        rounded
        small
        text
        @click="
          clearFilter(null, ['createdBy', 'role'])
          clearFilter([], ['assignees'])
        "
      >
        {{ $t('common.clear') }}
      </v-btn>
      <div class="mx-5 pt-1" data-testid="filters-section-users">
        <AssigneesSelector
          class="mb-4"
          data-testid="filters-select-created-by"
          hideDetails
          :availableAccounts="assignees"
          :disabled="isLoading"
          :isLoading="isSearching || isLoading"
          :label="$t('filters.createdBy')"
          :selectedAccountIds="selectedFilters.createdBy"
          @change="handleCreatedByChange"
          @search="handleSearch"
        />

        <AssigneesSelector
          class="mb-4"
          data-testid="filters-select-assignees"
          hideDetails
          multiple
          :availableAccounts="assignees"
          :disabled="isLoading"
          :isLoading="isSearching || isLoading"
          :label="$t('common.other_options.assignees')"
          :selectedAccountIds="selectedFilters.assignees"
          @change="$emit('change', $event, 'assignees')"
          @search="handleSearch"
        />

        <v-autocomplete
          clearable
          class="mb-4"
          data-testid="filters-select-role"
          dense
          hide-details
          item-text="text"
          item-value="id"
          outlined
          :items="roleOptions"
          :label="$t('filters.role')"
          :value="selectedFilters.role"
          @change="$emit('change', $event, 'role')"
        />
      </div>
    </template>

    <FilterHeader
      class="mt-4"
      :isActive="showFilterByType.location"
      :name="$t('common.location_and_categorization')"
      @change="showFilterByType.location = !showFilterByType.location"
    />
    <template v-if="showFilterByType.location">
      <v-btn
        v-show="isLocationSelected"
        class="mb-1 mt-0 font-size-12 ms-1"
        color="blue"
        data-testid="filters-button-clear-location"
        rounded
        small
        text
        @click="
          clearFilter(
            [],
            [
              'sites',
              'siteGroups',
              'tags',
              'tagGroups',
              'countryCodes',
              'priority',
              'condition',
              'categories',
              'situationTypes',
            ]
          )
        "
      >
        {{ $t('common.clear') }}
      </v-btn>
      <div class="mx-5 pt-1" data-testid="filters-section-location">
        <MultipleSelect
          class="mb-4"
          data-testid="filters-select-sites"
          dense
          hide-details
          item-value="id"
          outlined
          persistent-hint
          :label="$t('common.location_tags_options.site')"
          :loading="isLoading"
          :options="sortedSitesOptions"
          :value="selectedFilters.sites"
          @change="$emit('change', $event, 'sites')"
        />
        <MultipleSelect
          class="mb-4"
          data-testid="filters-select-site-groups"
          dense
          hide-details
          item-value="id"
          outlined
          :label="$t('common.location_tags_options.site_group')"
          :loading="isLoading"
          :options="sortedSiteGroupsOptions"
          :value="selectedFilters.siteGroups"
          @change="$emit('change', $event, 'siteGroups')"
        />

        <MultipleSelect
          class="mb-4"
          data-testid="filters-select-country-codes"
          dense
          hide-details
          item-value="id"
          outlined
          :label="$t('common.location_tags_options.country')"
          :loading="isLoading"
          :options="sortedCountryCodeOptions"
          :value="selectedFilters.countryCodes"
          @change="$emit('change', $event, 'countryCodes')"
        />

        <MultipleSelect
          class="mb-4"
          data-testid="filters-select-tags"
          dense
          hide-details
          item-value="id"
          outlined
          :label="$t('common.location_tags_options.tags')"
          :loading="isLoading"
          :options="sortedTagsOptions"
          :value="selectedFilters.tags"
          @change="$emit('change', $event, 'tags')"
        />

        <MultipleSelect
          class="mb-4"
          data-testid="filters-select-tag-groups"
          dense
          hide-details
          item-value="id"
          outlined
          :label="$t('common.location_tags_options.tag_groups')"
          :loading="isLoading"
          :options="sortedTagGroupsOptions"
          :value="selectedFilters.tagGroups"
          @change="$emit('change', $event, 'tagGroups')"
        />

        <MultipleSelect
          class="mb-4"
          data-testid="filters-select-priority"
          dense
          hide-details
          item-text="text"
          item-value="id"
          outlined
          :label="$t('filters.priority')"
          :options="priorityOptions"
          :value="selectedFilters.priority"
          @change="$emit('change', $event, 'priority')"
        />

        <MultipleSelect
          class="mb-4"
          data-testid="filters-select-condition"
          dense
          hide-details
          item-text="text"
          item-value="id"
          outlined
          :label="$t('filters.condition')"
          :options="sortedConditionOptions"
          :value="selectedFilters.condition"
          @change="$emit('change', $event, 'condition')"
        />

        <MultipleSelect
          class="mb-4"
          data-testid="filters-select-categories"
          hide-details
          item-text="text"
          item-value="value"
          outlined
          :label="$t('common.other_options.categories')"
          :options="sortedCategoryOptions"
          :value="selectedFilters.categories"
          @change="$emit('change', $event, 'categories')"
        />

        <MultipleSelect
          v-if="showRoutineFilters"
          class="mb-4"
          data-testid="filters-select-situation-types"
          hide-details
          item-text="text"
          item-value="value"
          outlined
          :label="$t('filters.situationTypes')"
          :loading="isLoadingSituationTypes"
          :options="sortedSituationTypeOptions"
          :value="selectedFilters.situationTypes"
          @change="$emit('change', $event, 'situationTypes')"
        />
      </div>
    </template>

    <FilterHeader
      v-if="showRoutineFilters"
      class="mt-4"
      :isActive="showFilterByType.routines"
      :name="$t('filters.routines')"
      @change="showFilterByType.routines = !showFilterByType.routines"
    />
    <template v-if="showRoutineFilters && showFilterByType.routines">
      <v-btn
        v-show="isRoutinesSelected"
        class="mb-1 mt-0 font-size-12 ms-1"
        color="blue"
        data-testid="filters-button-clear-routines"
        rounded
        small
        text
        @click="
          clearFilter([], ['routineDefinitions', 'routineType'])
          clearFilter(null, ['routineDefinitionsPresetFilter'])
        "
      >
        {{ $t('common.clear') }}
      </v-btn>
      <div class="mx-5 pt-1" data-testid="filters-section-routines">
        <MultipleSelect
          class="mb-4"
          data-testid="filters-select-routine-definitions"
          hide-details
          item-text="text"
          item-value="id"
          outlined
          :label="$t('filters.routineDefinitions')"
          :loading="isSearchingRoutineDefinitions"
          :options="sortedRoutineDefinitionsOptions"
          :value="selectedFilters.routineDefinitions"
          @change="$emit('change', $event, 'routineDefinitions')"
          @search="handleRoutineDefinitionsSearch"
        />

        <MultipleSelect
          class="mb-4"
          data-testid="filters-select-routine-type"
          hide-details
          item-text="text"
          item-value="id"
          outlined
          :label="$t('filters.routineType')"
          :options="sortedRoutineTypeOptions"
          :value="selectedFilters.routineType"
          @change="$emit('change', $event, 'routineType')"
        />
      </div>
    </template>

    <FilterHeader
      class="mt-4"
      :isActive="showFilterByType.date"
      :name="$t('common.date')"
      @change="showFilterByType.date = !showFilterByType.date"
    />
    <template v-if="showFilterByType.date">
      <v-btn
        v-show="isDateSelected"
        class="mb-1 mt-0 font-size-12 ms-1"
        color="blue"
        data-testid="filters-button-clear-date"
        rounded
        small
        text
        @click="clearDateFilter(['createdAt', 'completedAt', 'dueDate'])"
      >
        {{ $t('common.clear') }}
      </v-btn>
      <div class="mx-5 pt-1" data-testid="filters-section-date">
        <CreationDateSelector
          class="mb-4"
          :selectedFilters="selectedFilters"
          @change="handleDateChange"
        />
        <CompletedDateSelector
          class="mb-4"
          :selectedFilters="selectedFilters"
          @change="handleDateChange"
        />
        <DueDateSelector
          :selectedFilters="selectedFilters"
          @change="handleDateChange"
        />
      </div>
    </template>
  </div>
</template>

<script>
import _ from 'lodash'

import api from '@/api'
import store from '@/store/index.js'

import AssigneesSelector from '@/ui/shared/components/AssigneeSelector.vue'
import CompletedDateSelector from '@/ui/shared/components/CompletedDateSelector.vue'
import CreationDateSelector from '@/ui/shared/components/CreationDateSelector.vue'
import DueDateSelector from '@/ui/shared/components/DueDateSelector.vue'
import MultipleSelect from '@/ui/shared/components/MultipleSelect.vue'
import RadioFilter from '@/ui/shared/components/RadioFilter.vue'

import constants from './constants'
import {
  buildSituationTypeOption,
  buildCategoryOption,
  hasRoutineFilterSupport,
} from './filterState.js'
import FilterHeader from './FilterHeader.vue'

export default {
  name: 'Filters',

  components: {
    AssigneesSelector,
    CompletedDateSelector,
    CreationDateSelector,
    DueDateSelector,
    FilterHeader,
    MultipleSelect,
    RadioFilter,
  },

  props: {
    hasColumnSelector: { type: Boolean, required: false },
    selectedFilters: {
      type: Object,
      required: true,
    },
    showInstructions: { type: Boolean, required: false },
    typeOptions: {
      type: Array,
      required: false,
      default: () => [],
    },
  },

  data() {
    return {
      assignees: [],
      categories: [],
      countryCodes: [],
      isLoading: false,
      isLoadingSituationTypes: false,
      isSearching: false,
      isSearchingRoutineDefinitions: false,
      priorityOptions: constants.getPriorityOptions(),
      conditionOptions: constants.getConditionOptions(),
      roleOptions: constants.getRoleOptions(),
      validationOptions: constants.getValidationOptions(),
      validationRadioOptions: Object.values(constants.getValidationOptions()),
      routineTypeOptions: constants.getRoutineTypeOptions(),
      routineDefinitions: [],
      routineDefinitionsSearchTerm: '',
      selectedValidationOption: this.getInitialValidationOption(),
      showFilterByType: {
        date: true,
        location: true,
        routines: true,
        status: true,
        types: true,
        users: true,
        validation: true,
      },
      sites: [],
      siteGroups: [],
      situationTypes: [],
      statusOptions: [
        {
          id: 'open',
          text: this.$t('common.status_options.open'),
        },
        {
          id: 'inProgress',
          text: this.$t('common.status_options.in_progress'),
        },
        {
          id: 'completed',
          text: this.$t('common.status_options.completed'),
        },
        {
          id: 'onHold',
          text: this.$t('common.status_options.on_hold'),
        },
        {
          id: 'canceled',
          text: this.$t('common.status_options.canceled'),
        },
      ],
      tagGroups: [],
      tags: [],
      hasLoadedRoutineFilterOptions: false,
    }
  },

  mounted() {
    this.setStructure()

    if (this.showRoutineFilters && !this.hasLoadedRoutineFilterOptions) {
      this.hasLoadedRoutineFilterOptions = true
      this.fetchSituationTypes()
      this.fetchRoutineDefinitions()
    }
  },

  watch: {
    showRoutineFilters(newValue) {
      if (newValue && !this.hasLoadedRoutineFilterOptions) {
        this.hasLoadedRoutineFilterOptions = true
        this.fetchSituationTypes()
        this.fetchRoutineDefinitions()
      }
    },
  },

  computed: {
    organizationId() {
      return store.access.getOrganizationId()
    },

    showRoutineFilters() {
      return hasRoutineFilterSupport(this.typeOptions)
    },

    showClearAllFilters() {
      return Object.entries(this.selectedFilters).some(([, value]) => {
        if (_.isArray(value)) return !_.isEmpty(value)
        if (_.isPlainObject(value)) return !_.isEmpty(value)
        return !_.isNil(value) && value !== ''
      })
    },

    isRoutinesSelected() {
      return Boolean(
        this.selectedFilters?.routineDefinitions?.length ||
          this.selectedFilters?.routineDefinitionsPresetFilter ||
          this.selectedFilters?.routineType?.length
      )
    },

    isUsersSelected() {
      return Boolean(
        this.selectedFilters?.createdBy ||
          this.selectedFilters?.assignees?.length ||
          this.selectedFilters?.role
      )
    },

    isDateSelected() {
      return Boolean(
        !_.isEmpty(this.selectedFilters?.createdAt) ||
          !_.isEmpty(this.selectedFilters?.completedAt) ||
          !_.isEmpty(this.selectedFilters?.dueDate)
      )
    },

    isLocationSelected() {
      return Boolean(
        this.selectedFilters?.sites?.length ||
          this.selectedFilters?.siteGroups?.length ||
          this.selectedFilters?.countryCodes?.length ||
          this.selectedFilters?.tags?.length ||
          this.selectedFilters?.tagGroups?.length ||
          this.selectedFilters?.priority?.length ||
          this.selectedFilters?.condition?.length ||
          this.selectedFilters?.categories?.length ||
          this.selectedFilters?.situationTypes?.length
      )
    },

    isStatusSelected() {
      return Boolean(this.selectedFilters?.status?.length)
    },

    isTypeSelected() {
      return Boolean(this.selectedFilters?.types?.length)
    },

    countryCodeOptions() {
      return (
        this.countryCodes.map?.(countryCode => ({
          id: countryCode,
          name: this.$t(`countries.${countryCode}`),
        })) ?? []
      )
    },

    situationTypeOptions() {
      return this.situationTypes.map(situation => ({
        ...situation,
        text: situation?.text ?? situation?.name,
        value: situation?.value ?? situation?.name,
      }))
    },

    categoryOptions() {
      return this.categories.map(category => ({
        ...category,
        text: category?.text ?? category?.name,
        value: category?.value ?? category?.name,
      }))
    },

    sortedSitesOptions() {
      return this.sortOptionsAlphabetically(this.sites, ['name', 'text'])
    },

    sortedSiteGroupsOptions() {
      return this.sortOptionsAlphabetically(this.siteGroups, ['name', 'text'])
    },

    sortedCountryCodeOptions() {
      return this.sortOptionsAlphabetically(this.countryCodeOptions, ['name'])
    },

    sortedTagsOptions() {
      return this.sortOptionsAlphabetically(this.tags, ['name', 'text'])
    },

    sortedTagGroupsOptions() {
      return this.sortOptionsAlphabetically(this.tagGroups, ['name', 'text'])
    },

    sortedConditionOptions() {
      return this.sortOptionsAlphabetically(this.conditionOptions, ['text'])
    },

    sortedCategoryOptions() {
      return this.sortOptionsAlphabetically(this.categoryOptions, [
        'text',
        'name',
        'value',
      ])
    },

    sortedSituationTypeOptions() {
      return this.sortOptionsAlphabetically(this.situationTypeOptions, [
        'text',
        'name',
        'value',
      ])
    },

    sortedRoutineTypeOptions() {
      return this.sortOptionsAlphabetically(this.routineTypeOptions, ['text'])
    },

    sortedRoutineDefinitionsOptions() {
      if (!this.routineDefinitionsSearchTerm) {
        return this.sortOptionsAlphabetically(this.routineDefinitions, ['text'])
      }

      return _.orderBy(
        this.routineDefinitions,
        [
          routine => Number(routine?._score ?? Number.NEGATIVE_INFINITY),
          routine => `${routine?.text ?? ''}`.toLowerCase(),
        ],
        ['desc', 'asc']
      )
    },
  },

  methods: {
    clearAllFilters() {
      this.$track('msOffice.clearAllFilters', {
        env: window.Office.context.host,
      })

      this.selectedValidationOption = null

      Object.keys(this.selectedFilters).forEach(key => {
        const value = this.selectedFilters[key]
        this.$emit('change', _.isArray(value) ? [] : null, key)
      })
    },

    clearDateFilter(keys) {
      _.forEach(
        ['dueDateOption', 'createdAtOption', 'completedAtOption'],
        key => {
          this.$emit('change', null, key)
        }
      )
      this.clearFilter([], keys)
    },

    clearFilter(value, filters) {
      this.$track('msOffice.clearFilters', {
        env: window.Office.context.host,
        filtersCleared: filters,
      })

      _.forEach(filters, filter => {
        this.$emit('change', value, filter)
      })
    },

    clearValidationFilter() {
      this.selectedValidationOption = null

      this.clearFilter(null, [
        'hasImplementationValidation',
        'hasFinancialValidation',
      ])
    },

    async fetchAccounts(terms = '') {
      try {
        this.isSearching = true
        const { data } = await api.stream.getAccounts(this.organizationId, {
          terms,
        })
        this.assignees = data?.results ?? []
      } catch (error) {
        console.log(error)
      } finally {
        this.isSearching = false
      }
    },

    async fetchRoutineDefinitions(terms = '') {
      try {
        this.isSearchingRoutineDefinitions = true
        const normalizedTerms = (terms ?? '').trim()

        this.routineDefinitionsSearchTerm = normalizedTerms

        const { data } = await api.routine.get(this.organizationId, {
          terms: normalizedTerms,
          sort: normalizedTerms
            ? { by: '_score', order: 'desc' }
            : { by: 'name', order: 'asc' },
        })

        this.routineDefinitions = (data?.results ?? data ?? []).map(
          routine => ({
            id: routine?.id ?? routine?._id,
            text: routine?.name,
            _score: routine?._score,
          })
        )
      } catch (error) {
        console.log(error)
      } finally {
        this.isSearchingRoutineDefinitions = false
      }
    },

    sortOptionsAlphabetically(options = [], keys = ['text', 'name', 'value']) {
      return _.orderBy(
        options,
        option => {
          const key = keys.find(candidate => {
            const value = option?.[candidate]
            return !_.isNil(value) && `${value}`.trim() !== ''
          })

          return `${option?.[key] ?? ''}`.toLowerCase()
        },
        'asc'
      )
    },

    async fetchSituationTypes() {
      try {
        this.isLoadingSituationTypes = true
        const { data } = await api.situationType.getOrganizationCatalog(
          this.organizationId
        )
        this.situationTypes = (data ?? []).map(situation =>
          buildSituationTypeOption(situation, key => this.$t(key))
        )
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoadingSituationTypes = false
      }
    },

    getInitialValidationOption() {
      if (
        this.selectedFilters.hasImplementationValidation &&
        this.selectedFilters.hasFinancialValidation
      ) {
        return this.validationOptions.impactValidated.id
      }

      if (
        this.selectedFilters.hasImplementationValidation &&
        this.selectedFilters.hasFinancialValidation === false
      ) {
        return this.validationOptions.implemented.id
      }

      if (
        this.selectedFilters.hasImplementationValidation === false &&
        this.selectedFilters.hasFinancialValidation === false
      ) {
        return this.validationOptions.notImplemented.id
      }
    },

    handleCreatedByChange(value) {
      const selectedAccount = _.isArray(value) ? value[0] : value
      this.$emit(
        'change',
        selectedAccount?.id ?? selectedAccount ?? null,
        'createdBy'
      )
    },

    handleDateChange(values, key) {
      this.$emit('change', values, key)
    },

    handleRoutineDefinitionsSearch(terms) {
      this.fetchRoutineDefinitions(terms)
    },

    handleSearch(terms) {
      this.fetchAccounts(terms)
    },

    handleValidationOptionChange(value) {
      this.selectedValidationOption = value

      switch (this.selectedValidationOption) {
        case this.validationOptions.impactValidated.id:
          this.$emit('change', true, 'hasImplementationValidation')
          this.$emit('change', true, 'hasFinancialValidation')
          break
        case this.validationOptions.implemented.id:
        case this.validationOptions.impactNotValidated.id:
          this.$emit('change', true, 'hasImplementationValidation')
          this.$emit('change', false, 'hasFinancialValidation')
          break
        case this.validationOptions.notImplemented.id:
          this.$emit('change', false, 'hasImplementationValidation')
          this.$emit('change', false, 'hasFinancialValidation')
          break
        default:
          return null
      }
    },

    setStructure() {
      try {
        this.isLoading = true
        const organizationStructure = store.organization.getOrganizationStructure()
        const countryCodes = store.organization.getCountyCodes() ?? []
        const organizationCategories =
          store.organization.getOrganizationCategories() ?? []

        this.sites = organizationStructure?.sites ?? []
        this.siteGroups = organizationStructure?.siteGroups ?? []
        this.tags = organizationStructure?.tags ?? []
        this.tagGroups = organizationStructure?.tagGroups ?? []
        this.countryCodes = countryCodes
        this.categories = organizationCategories.map(category =>
          buildCategoryOption(category, key => this.$t(key))
        )
        this.fetchAccounts()
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>
